import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { toFileExtensionLabel, toFileExtensionTone, toFileSizeText } from "./snapshotDropzone.utils";

interface SnapshotDropzoneRowProps {
  name: string;
  /** Hover text for the name (e.g. the S3 key). */
  title: string;
  size: number;
  status: ReactNode;
  action: ReactNode;
  /** Full width under the row (the progress bar); collapses away when `showProgress` turns false. */
  progress?: ReactNode;
  showProgress?: boolean;
  /** Identifies the action, so a new kind of action fades in. */
  actionKey?: string;
}

interface SnapshotDropzoneExtensionBadgeProps {
  filename: string;
}

const SnapshotDropzoneExtensionBadge = (props: SnapshotDropzoneExtensionBadgeProps) => {
  const { filename } = props;
  const tone = toFileExtensionTone(filename);
  let bgcolor = "action.hover";
  let color = "text.secondary";
  if (tone !== "neutral") {
    bgcolor = `surface.${tone}`;
    color = `${tone}.main`;
  }

  return (
    <Box
      aria-hidden
      sx={{
        alignItems: "center",
        bgcolor,
        borderRadius: 0.75,
        color,
        display: "flex",
        flexShrink: 0,
        fontFamily: "monospace",
        fontSize: 11,
        fontWeight: 700,
        height: 28,
        justifyContent: "center",
        letterSpacing: "0.04em",
        width: 40,
      }}
    >
      {toFileExtensionLabel(filename).slice(0, 4)}
    </Box>
  );
};

/** A file row: extension badge, the name over "size · status", and the row's action on the right. */
export const SnapshotDropzoneRow = (props: SnapshotDropzoneRowProps) => {
  const { name, title, size, status, action, progress, showProgress = true, actionKey } = props;
  return (
    <Box
      sx={{
        borderRadius: 0.75,
        mb: "2px",
        pl: 1.5,
        pr: 1,
        py: 1,
        transition: "background-color 120ms",
        "&:hover, &:focus-within": { bgcolor: "action.hover" },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: "center",
          minHeight: 36,
        }}
      >
        <SnapshotDropzoneExtensionBadge filename={name} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Tooltip title={title}>
            <Typography
              noWrap
              sx={{ fontFamily: "monospace" }}
              variant="body2"
            >
              {name}
            </Typography>
          </Tooltip>
          <Typography
            color="text.secondary"
            sx={{ display: "block" }}
            variant="caption"
          >
            {toFileSizeText(size)} · {status}
          </Typography>
        </Box>
        <Fade
          in
          key={actionKey}
        >
          <Box sx={{ flexShrink: 0 }}>{action}</Box>
        </Fade>
      </Stack>
      {progress && <Collapse in={showProgress}>{progress}</Collapse>}
    </Box>
  );
};
